import { match } from '@st/pattern'

export type Schema = {
  'type'?: string | string[]
  'source_type'?: string
  'title'?: string
  'format'?: string
  'enum'?: Array<string | number | boolean | null>
  'properties'?: Record<string, Schema>
  'items'?: Schema
  'required'?: string[]
  'default'?: unknown
  'minimum'?: number
  'maximum'?: number
  'x-hidden'?: boolean
  'x-order'?: number
}

// Array addresses use session identities, never positions. These identities
// exist only in editor state; document data and JSON patches contain no UI IDs.
export type Address = Array<string | number>
export type Node =
  | { id: number; kind: 'object'; fields: Record<string, Node> }
  | { id: number; kind: 'array'; items: Node[] }
  | { id: number; kind: 'scalar'; value: unknown }
export type EditorState = {
  root: Node
  // Last reconciled document snapshot, used for value-based dirty detection.
  base: Node
  nextId: number
  creation: { parent: Address; root: Node } | null
  collapsed: number[]
}
export type Scope = 'document' | 'creation'
export type Edit =
  | { type: 'change'; scope: Scope; path: Address; value: unknown }
  | { type: 'add'; path: Address; value: unknown }
  | { type: 'remove'; path: Address; id: number }
  | { type: 'commit' }
  | { type: 'cancel' }
  | { type: 'toggle'; id: number }

export function schemaType(schema: Schema): string | undefined {
  if (!Array.isArray(schema.type)) return schema.type
  const types = schema.type.filter((type) => type != 'null')
  if (types.length != 1 || types[0] == 'object' || types[0] == 'array')
    throw new Error('Only nullable scalar unions are supported')
  return types[0]
}

export function properties(schema: Schema) {
  return Object.entries(schema.properties ?? {})
    .filter(([, child]) => !child['x-hidden'])
    .sort(([a, left], [b, right]) => {
      const rank = (key: string, child: Schema) => {
        if (child['x-order'] != undefined) return child['x-order']
        if (schemaType(child) == 'array') return 30
        if (schema.required?.includes(key)) return 0
        if (schemaType(child) == 'object') return 20
        return 10
      }
      return rank(a, left) - rank(b, right) || a.localeCompare(b)
    })
}

export function labelFor(schema: Schema, fallback: string): string {
  return (
    schema.title ??
    fallback
      .replaceAll('_', ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase())
  )
}

export function initialValue(schema: Schema): unknown {
  if (schema.default !== undefined) return structuredClone(schema.default)
  if (schema.enum) return undefined
  return match(schemaType(schema))
    .with('boolean', () => false)
    .with('array', () => [])
    .with('object', () =>
      Object.fromEntries(
        properties(schema)
          .filter(([key]) => schema.required?.includes(key))
          .map(([key, child]) => [key, initialValue(child)])
      )
    )
    .with('string', () => '')
    .otherwise(() => undefined)
}

function tree(value: unknown, allocate: () => number): Node {
  const id = allocate()
  if (Array.isArray(value))
    return { id, kind: 'array', items: value.map((item) => tree(item, allocate)) }
  if (value != null && typeof value == 'object')
    return {
      id,
      kind: 'object',
      fields: Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, tree(item, allocate)])
      )
    }
  return { id, kind: 'scalar', value }
}

export function createEditor(value: unknown): EditorState {
  let nextId = 0
  const root = tree(value, () => nextId++)
  return { root, base: root, nextId, creation: null, collapsed: [] }
}

export function valueOf(node: Node | undefined): unknown {
  if (!node) return undefined
  return match(node)
    .with({ kind: 'scalar' }, (node) => node.value)
    .with({ kind: 'array' }, (node) => node.items.map(valueOf))
    .with({ kind: 'object' }, (node) =>
      Object.fromEntries(
        Object.entries(node.fields)
          .map(([key, child]) => [key, valueOf(child)])
          .filter(([, value]) => value !== undefined)
      )
    )
    .exhaustive()
}

export function at(root: Node | undefined, path: Address): Node | undefined {
  return path.reduce<Node | undefined>((node, key) => {
    if (!node) return undefined
    if (typeof key == 'number') {
      if (node.kind != 'array') throw new Error('Item address requires an array')
      return node.items.find((item) => item.id == key)
    }
    if (node.kind != 'object') return undefined
    return node.fields[key]
  }, root)
}

function modify(
  root: Node | undefined,
  path: Address,
  change: (node: Node | undefined) => Node,
  allocate: () => number
): Node {
  if (path.length == 0) return change(root)
  const [key, ...rest] = path
  if (typeof key == 'number') {
    if (root?.kind != 'array' || !root.items.some((item) => item.id == key))
      throw new Error('Missing array item')
    return {
      ...root,
      items: root.items.map((item) => {
        if (item.id != key) return item
        return modify(item, rest, change, allocate)
      })
    }
  }
  const node = root ?? { id: allocate(), kind: 'object' as const, fields: {} }
  if (node.kind != 'object') throw new Error('Property address requires an object')
  return {
    ...node,
    fields: { ...node.fields, [key]: modify(node.fields[key], rest, change, allocate) }
  }
}

// A replaced subtree keeps the replaced node's identity; its own allocated id
// is spent, never reused, so identities stay unique for the session.
function replacing(value: unknown, allocate: () => number) {
  return (node: Node | undefined): Node => {
    const replacement = tree(value, allocate)
    if (node) return { ...replacement, id: node.id }
    return replacement
  }
}

function removing(id: number) {
  return (node: Node | undefined): Node => {
    if (node?.kind != 'array') throw new Error('Remove requires an array')
    return { ...node, items: node.items.filter((item) => item.id != id) }
  }
}

function appending(item: Node, allocate: () => number) {
  return (node: Node | undefined): Node => {
    if (!node) return { id: allocate(), kind: 'array', items: [item] }
    if (node.kind != 'array') throw new Error('Append requires an array')
    return { ...node, items: [...node.items, item] }
  }
}

export function updateEditor(state: EditorState, edit: Edit): EditorState {
  let nextId = state.nextId
  const allocate = () => nextId++
  const creation = () => {
    if (!state.creation) throw new Error('No item being created')
    return state.creation
  }
  const next = match(edit)
    .returnType<EditorState>()
    .with({ type: 'toggle' }, ({ id }) => {
      if (state.collapsed.includes(id))
        return { ...state, collapsed: state.collapsed.filter((other) => other != id) }
      return { ...state, collapsed: [...state.collapsed, id] }
    })
    .with({ type: 'add' }, ({ path, value }) => {
      if (state.creation) throw new Error('Finish the current creation first')
      return { ...state, creation: { parent: path, root: tree(value, allocate) } }
    })
    .with({ type: 'cancel' }, () => ({ ...state, creation: null }))
    .with({ type: 'commit' }, () => {
      const { parent, root } = creation()
      return {
        ...state,
        root: modify(state.root, parent, appending(root, allocate), allocate),
        creation: null
      }
    })
    .with({ type: 'change', scope: 'creation' }, ({ path, value }) => {
      const draft = creation()
      return {
        ...state,
        creation: { ...draft, root: modify(draft.root, path, replacing(value, allocate), allocate) }
      }
    })
    .with({ type: 'change', scope: 'document' }, ({ path, value }) => ({
      ...state,
      root: modify(state.root, path, replacing(value, allocate), allocate)
    }))
    .with({ type: 'remove' }, ({ path, id }) => {
      const root = modify(state.root, path, removing(id), allocate)
      // Removing any ancestor item of the draft also discards the draft.
      if (state.creation?.parent.includes(id)) return { ...state, root, creation: null }
      return { ...state, root }
    })
    .exhaustive()
  return { ...next, nextId }
}
